import axios from "axios";
import router from "@/router";
import { TokenStorage } from "@/services/TokenStorage";
import IAM from "@/services/api/IAM.js";
import store from "@/store";
import { postLoginRedirect } from "@/services/postLoginRedirect";

const axiosInstance = axios.create();
axiosInstance.defaults.baseURL = process.env["VUE_APP_API_URL"];

let refreshPromise = null;

// The session can no longer be recovered: drop both the Device Management token
// and the Guards token (leaving a stale admin_token behind makes the SDK think
// there is still a Guards session), remember the current page, and bounce to login.
function forceReauthentication() {
  postLoginRedirect.capture();
  TokenStorage.clear();
  localStorage.removeItem("admin_token");
  if (router.currentRoute.name !== "Login") {
    router.push({ name: "Login" });
  }
}

axiosInstance.interceptors.request.use((config) => {
  store.commit("global/SET_LOADING", true);
  config.headers = {
    ...config.headers,
    ...TokenStorage.getAuthentication().headers,
  };
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    store.commit("global/SET_LOADING", false);
    return response;
  },
  (error) => {
    store.commit("global/SET_LOADING", false);

    if (!error.response) {
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    if (status === 400 && data.message === "action expired") {
      store.commit("SET_MESSAGE_TYPE", "LinkExpired");
      router.push({ name: "Success" });
      return Promise.reject(error);
    }

    if (status === 400 && data.error_description === "Account is not fully set up") {
      const userEmail = store.getters.getUserEmail;
      return IAM.passwordUpdate(userEmail).then(() => {
        store.commit("SET_MESSAGE_TYPE", "AccountNotFullySetUp");
        router.push({ name: "Success" });
        return Promise.reject(error);
      });
    }

    if (status === 400) {
      return Promise.reject(error);
    }

    if (status === 409) {
      store.commit("SET_LOCATION_DISPLAY_ID", data.message);
      return Promise.reject(error);
    }

    if (status === 404 && data.message === "space-account not found") {
      store.commit("SET_MESSAGE_TYPE", "UserNotAllowed");
      router.push({ name: "Success" });
      return Promise.reject(error);
    }

    if (status === 405) {
      if (data.message === "Not allowed. Reset password request has been already sent. Please check your e-mail inbox.") {
        store.commit("SET_MESSAGE_TYPE", "MethodNotAllowed");
        router.push({ name: "Success" });
        return Promise.reject(error);
      } else if (data.message === "Not allowed for non-active accounts") {
        store.commit("SET_MESSAGE_TYPE", "UserNonExist");
        return Promise.reject(error);
      }
    }

    if (status === 503) {
      if (data.message === "This location has no available devices") {
        store.commit("devices/SET_NO_DETECT_DEVICES", true);
        store.commit("locations/SET_DETECTED_DEVICES", []);
      } else if (data.message === "physical device temporarily unavailable") {
        store.dispatch("global/addAlert", { type: "error", message: data.message }, { root: true });
      }
      return Promise.reject(error);
    }

    if (status === 422) {
      forceReauthentication();
      return Promise.reject(error);
    }

    if (status === 401) {
      const config = error.config;

      // 1. stop infinite retry
      if (config._retry) {
        return Promise.reject(error);
      }
      config._retry = true;

      // 2. skip auth endpoints (login/refresh)
      if (config.url?.includes("/openid-connect/token")) {
        forceReauthentication();
        return Promise.reject(error);
      }

      // 3. no refresh token → logout immediately
      const refreshToken = TokenStorage.getRefreshToken();
      if (!refreshToken) {
        forceReauthentication();
        return Promise.reject(error);
      }

      // 4. single shared refresh promise
      if (!refreshPromise) {
        refreshPromise = TokenStorage.getNewAccessToken()
          .catch((err) => {
            forceReauthentication();
            throw err;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      // 5. retry with new token
      return refreshPromise.then((token) => {
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${token}`;
        return axiosInstance.request(config);
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
